import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Plus, Trash2, GripVertical, Play, CheckCircle, XCircle,
  Calculator, Code, Blocks, ChevronDown, ChevronUp, Info,
  ArrowRight, Equal
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMULA_BLOCKS = [
  { type: 'variable', label: 'Variable', icon: 'x', color: 'bg-blue-500' },
  { type: 'operator', label: '+', icon: '+', color: 'bg-zinc-600' },
  { type: 'operator', label: '-', icon: '-', color: 'bg-zinc-600' },
  { type: 'operator', label: '*', icon: '×', color: 'bg-zinc-600' },
  { type: 'operator', label: '/', icon: '÷', color: 'bg-zinc-600' },
  { type: 'operator', label: '(', icon: '(', color: 'bg-zinc-500' },
  { type: 'operator', label: ')', icon: ')', color: 'bg-zinc-500' },
  { type: 'number', label: 'Number', icon: '#', color: 'bg-green-500' },
];

const FORMULA_TEMPLATES = [
  { name: 'COGS (Cost of Goods)', formula: 'pp + isc_amount + customs_amount', description: 'Purchase Price + ISC + Customs' },
  { name: 'DBP (Dealer Base Price)', formula: 'cogs / (1 - gp_percent/100)', description: 'COGS ÷ (1 - GP%)' },
  { name: 'MRP (Max Retail Price)', formula: 'dbp / (1 - ddr_percent/100)', description: 'DBP ÷ (1 - DDR%)' },
  { name: 'CBP (Cost Before GST)', formula: 'mrp / 1.18', description: 'MRP ÷ 1.18' },
  { name: 'Dealer Margin', formula: 'cbp - dbp', description: 'CBP - DBP' },
  { name: 'Percentage Calculation', formula: 'base_value * percent / 100', description: 'Base × Percent ÷ 100' },
  { name: 'Markup Price', formula: 'cost * (1 + markup_percent/100)', description: 'Cost × (1 + Markup%)' },
];

const FormulaRow = ({
  formula,
  index,
  onUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
  availableVariables,
  isFirst,
  isLast,
  onValidate,
  validationResult
}) => {
  const [showHelp, setShowHelp] = useState(false);
  const [mode, setMode] = useState('text');

  return (
    <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-lg p-4 border border-zinc-200 dark:border-zinc-700">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <GripVertical className="w-4 h-4 text-zinc-500 cursor-grab" />
          <span className="text-xs text-zinc-500 bg-zinc-200 dark:bg-zinc-700 px-2 py-0.5 rounded">
            #{index + 1}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-zinc-200 dark:bg-zinc-700 rounded-md p-0.5">
            <button
              type="button"
              onClick={() => setMode('text')}
              className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
                mode === 'text' ? 'bg-white dark:bg-zinc-600 text-zinc-900 dark:text-white' : 'text-zinc-500 dark:text-zinc-400'
              }`}
            >
              <Code className="w-3 h-3" /> Text
            </button>
            <button
              type="button"
              onClick={() => setMode('visual')}
              className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
                mode === 'visual' ? 'bg-white dark:bg-zinc-600 text-zinc-900 dark:text-white' : 'text-zinc-500 dark:text-zinc-400'
              }`}
            >
              <Blocks className="w-3 h-3" /> Visual
            </button>
          </div>

          <Button type="button" variant="ghost" size="sm" onClick={onMoveUp} disabled={isFirst} className="h-7 w-7 p-0">
            <ChevronUp className="w-4 h-4" />
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onMoveDown} disabled={isLast} className="h-7 w-7 p-0">
            <ChevronDown className="w-4 h-4" />
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onDelete} className="h-7 w-7 p-0 text-red-400 hover:text-red-300">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <Label className="text-xs text-zinc-500 dark:text-zinc-400 mb-1">Output Variable Name</Label>
          <Input
            value={formula.key || ''}
            onChange={(e) => onUpdate({ ...formula, key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
            placeholder="e.g., cogs, dbp, mrp"
            className="h-8 text-sm bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700"
          />
        </div>
        <div>
          <Label className="text-xs text-zinc-500 dark:text-zinc-400 mb-1">Display Name</Label>
          <Input
            value={formula.name || ''}
            onChange={(e) => onUpdate({ ...formula, name: e.target.value })}
            placeholder="e.g., Cost of Goods Sold"
            className="h-8 text-sm bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700"
          />
        </div>
      </div>

      {mode === 'text' ? (
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-xs text-zinc-500 dark:text-zinc-400">Formula (Excel-like)</Label>
            <button
              type="button"
              onClick={() => setShowHelp(!showHelp)}
              className="text-xs text-blue-500 dark:text-blue-400 hover:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1"
            >
              <Info className="w-3 h-3" /> Help
            </button>
          </div>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-green-500 dark:text-green-400 font-mono">=</span>
              <Input
                value={formula.formula || ''}
                onChange={(e) => onUpdate({ ...formula, formula: e.target.value })}
                placeholder="cogs / (1 - gp_percent/100)"
                className="h-9 pl-7 font-mono text-sm bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700"
              />
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => onValidate(formula)} className="h-9 px-3">
              <Play className="w-4 h-4" />
            </Button>
          </div>

          {validationResult && (
            <div className={`mt-2 text-xs flex items-center gap-1 ${
              validationResult.valid ? 'text-green-500 dark:text-green-400' : 'text-red-500 dark:text-red-400'
            }`}>
              {validationResult.valid ? (
                <>
                  <CheckCircle className="w-3 h-3" />
                  <span>Valid formula. Test result: {validationResult.result}</span>
                </>
              ) : (
                <>
                  <XCircle className="w-3 h-3" />
                  <span>{validationResult.error}</span>
                </>
              )}
            </div>
          )}

          {showHelp && (
            <div className="mt-3 p-3 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg text-xs">
              <p className="text-zinc-500 dark:text-zinc-400 mb-2">Available variables from previous steps:</p>
              <div className="flex flex-wrap gap-1 mb-3">
                {availableVariables.map(v => (
                  <code
                    key={v}
                    className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-800/50"
                    onClick={() => onUpdate({ ...formula, formula: (formula.formula || '') + v })}
                  >
                    {v}
                  </code>
                ))}
              </div>
              <p className="text-zinc-500 dark:text-zinc-400 mb-1">Operators: <code>+ - * / ( )</code></p>
              <p className="text-zinc-500 dark:text-zinc-400">Functions: <code>abs() round() min() max()</code></p>
            </div>
          )}
        </div>
      ) : (
        <VisualFormulaBuilder
          formula={formula}
          onUpdate={onUpdate}
          availableVariables={availableVariables}
        />
      )}

      <div className="mt-3">
        <Label className="text-xs text-zinc-500 dark:text-zinc-400 mb-1">Description (optional)</Label>
        <Input
          value={formula.description || ''}
          onChange={(e) => onUpdate({ ...formula, description: e.target.value })}
          placeholder="Brief description of this calculation"
          className="h-8 text-sm bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700"
        />
      </div>
    </div>
  );
};

const VisualFormulaBuilder = ({ formula, onUpdate, availableVariables }) => {
  const appendToFormula = (text) => {
    const newFormula = (formula.formula || '') + (formula.formula ? ' ' : '') + text;
    onUpdate({ ...formula, formula: newFormula.trim() });
  };

  return (
    <div className="space-y-3">
      <Label className="text-xs text-zinc-500 dark:text-zinc-400">Build formula visually:</Label>

      <div className="p-3 bg-white dark:bg-zinc-900 rounded border border-zinc-200 dark:border-zinc-700 min-h-[40px] font-mono text-sm">
        <span className="text-green-500 dark:text-green-400">=</span>{' '}
        <span className="text-zinc-900 dark:text-white">{formula.formula || <span className="text-zinc-400">Click buttons below to build formula</span>}</span>
      </div>

      <div className="flex flex-wrap gap-2">
        <span className="text-xs text-zinc-500 w-full mb-1">Variables:</span>
        {availableVariables.map(v => (
          <Button
            type="button"
            key={v}
            variant="outline"
            size="sm"
            onClick={() => appendToFormula(v)}
            className="h-7 text-xs bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-800/50"
          >
            {v}
          </Button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <span className="text-xs text-zinc-500 w-full mb-1">Operators:</span>
        {['+', '-', '*', '/', '(', ')', '1', '100'].map(op => (
          <Button
            type="button"
            key={op}
            variant="outline"
            size="sm"
            onClick={() => appendToFormula(op)}
            className="h-7 w-8 text-xs"
          >
            {op === '*' ? '×' : op === '/' ? '÷' : op}
          </Button>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onUpdate({ ...formula, formula: '' })}
          className="h-7 text-xs text-red-500 dark:text-red-400 border-red-300 dark:border-red-700"
        >
          Clear
        </Button>
      </div>
    </div>
  );
};

const FormulaPlayground = ({ formulas, pricingFields, onClose }) => {
  const [inputValues, setInputValues] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const initial = { pp: 1000 };
    pricingFields.forEach(field => {
      initial[field.key] = field.percent || 0;
      initial[`${field.key}_amount`] = (1000 * (field.percent || 0)) / 100;
    });
    setInputValues(initial);
  }, [pricingFields]);

  const runPlayground = async () => {
    setLoading(true);
    try {
      const pp = inputValues.pp || 1000;
      const updatedInputs = { ...inputValues };
      pricingFields.forEach(field => {
        updatedInputs[`${field.key}_amount`] = pp * (updatedInputs[field.key] || 0) / 100;
      });

      const response = await axios.post(`${API}/formulas/evaluate`, {
        formulas: formulas,
        input_values: updatedInputs
      });
      setResults(response.data);
      toast.success('Formulas evaluated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to evaluate formulas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-zinc-200 dark:border-zinc-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calculator className="w-6 h-6 text-[#C3002F]" />
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">Formula Playground</h2>
          </div>
          <Button variant="ghost" onClick={onClose}>×</Button>
        </div>

        <div className="flex-1 overflow-auto p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-zinc-900 dark:text-white mb-3 flex items-center gap-2">
                <span className="w-6 h-6 rounded bg-blue-600 flex items-center justify-center text-xs text-white">1</span>
                Input Values
              </h3>
              <div className="space-y-3 bg-zinc-50 dark:bg-zinc-800/50 rounded-lg p-4">
                <div>
                  <Label className="text-xs text-zinc-500 dark:text-zinc-400">Purchase Price (pp)</Label>
                  <Input
                    type="number"
                    value={inputValues.pp || ''}
                    onChange={(e) => setInputValues({ ...inputValues, pp: parseFloat(e.target.value) || 0 })}
                    className="h-9 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700"
                  />
                </div>

                {pricingFields.map(field => (
                  <div key={field.key}>
                    <Label className="text-xs text-zinc-500 dark:text-zinc-400">{field.label} ({field.key})</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        value={inputValues[field.key] || ''}
                        onChange={(e) => setInputValues({ ...inputValues, [field.key]: parseFloat(e.target.value) || 0 })}
                        className="h-9 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700"
                        placeholder="%"
                      />
                      <span className="text-zinc-500 flex items-center">%</span>
                    </div>
                  </div>
                ))}

                <Button
                  onClick={runPlayground}
                  disabled={loading}
                  className="w-full bg-[#C3002F] hover:bg-[#a00025] mt-4"
                >
                  {loading ? 'Calculating...' : (
                    <>
                      <Play className="w-4 h-4 mr-2" /> Run Calculations
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-zinc-900 dark:text-white mb-3 flex items-center gap-2">
                <span className="w-6 h-6 rounded bg-green-600 flex items-center justify-center text-xs text-white">2</span>
                Step-by-Step Results
              </h3>
              <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-lg p-4 min-h-[300px]">
                {results ? (
                  <div className="space-y-3">
                    {results.steps.map((step, idx) => (
                      <div
                        key={idx}
                        className={`p-3 rounded-lg border ${
                          step.status === 'success'
                            ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
                            : 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-zinc-900 dark:text-white">{step.name}</span>
                          <span className={`text-lg font-mono font-bold ${
                            step.status === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'
                          }`}>
                            {step.status === 'success'
                              ? `₹${step.result?.toLocaleString()}`
                              : 'Error'
                            }
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <code className="text-zinc-500 dark:text-zinc-400 bg-zinc-100 dark:bg-zinc-900 px-2 py-0.5 rounded">
                            {step.key}
                          </code>
                          <ArrowRight className="w-3 h-3 text-zinc-400" />
                          <code className="text-blue-500 dark:text-blue-400">= {step.formula}</code>
                        </div>
                        {step.status === 'error' && (
                          <p className="text-xs text-red-500 dark:text-red-400 mt-1">{step.error}</p>
                        )}
                      </div>
                    ))}

                    <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-700">
                      <h4 className="text-xs text-zinc-500 dark:text-zinc-400 mb-2">All Variables:</h4>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(results.final_variables || {}).map(([key, value]) => (
                          <div key={key} className="text-xs bg-zinc-100 dark:bg-zinc-900 px-2 py-1 rounded">
                            <span className="text-zinc-500 dark:text-zinc-400">{key}:</span>{' '}
                            <span className="text-zinc-900 dark:text-white font-mono">
                              {typeof value === 'number' ? value.toLocaleString() : value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-zinc-400">
                    <Calculator className="w-12 h-12 mb-3 opacity-50" />
                    <p>Click "Run Calculations" to see results</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function FormulaBuilder({
  formulas = [],
  onChange,
  pricingFields = []
}) {
  const [showPlayground, setShowPlayground] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [validationResults, setValidationResults] = useState({});

  const getAvailableVariables = (upToIndex) => {
    const vars = ['pp', 'customs_duty', 'exchange_rate'];

    pricingFields.forEach(field => {
      vars.push(field.key);
      vars.push(`${field.key}_amount`);
    });

    formulas.slice(0, upToIndex).forEach(f => {
      if (f.key) vars.push(f.key);
    });

    return vars;
  };

  const addFormula = () => {
    const newFormulas = [
      ...formulas,
      { key: '', name: '', formula: '', order: formulas.length, description: '' }
    ];
    onChange(newFormulas);
  };

  const updateFormula = (index, updatedFormula) => {
    const newFormulas = [...formulas];
    newFormulas[index] = updatedFormula;
    onChange(newFormulas);
  };

  const deleteFormula = (index) => {
    const newFormulas = formulas.filter((_, i) => i !== index);
    newFormulas.forEach((f, i) => f.order = i);
    onChange(newFormulas);
  };

  const moveFormula = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= formulas.length) return;

    const newFormulas = [...formulas];
    [newFormulas[index], newFormulas[newIndex]] = [newFormulas[newIndex], newFormulas[index]];
    newFormulas.forEach((f, i) => f.order = i);
    onChange(newFormulas);
  };

  const validateFormula = async (formula, index) => {
    try {
      const vars = { pp: 1000 };
      pricingFields.forEach(field => {
        vars[field.key] = field.percent || 10;
        vars[`${field.key}_amount`] = 100;
      });
      formulas.slice(0, index).forEach(f => {
        if (f.key) vars[f.key] = 1000;
      });

      const response = await axios.post(`${API}/formulas/validate`, {
        formula: formula.formula,
        variables: vars
      });

      setValidationResults(prev => ({
        ...prev,
        [index]: response.data
      }));
    } catch (error) {
      setValidationResults(prev => ({
        ...prev,
        [index]: { valid: false, error: 'Failed to validate' }
      }));
    }
  };

  const applyTemplate = (template) => {
    const newFormulas = [
      ...formulas,
      {
        key: template.name.toLowerCase().replace(/[^a-z0-9]/g, '_'),
        name: template.name,
        formula: template.formula,
        order: formulas.length,
        description: template.description
      }
    ];
    onChange(newFormulas);
    setShowTemplates(false);
    toast.success(`Added template: ${template.name}`);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-zinc-900 dark:text-white flex items-center gap-2">
            <Calculator className="w-4 h-4 text-[#C3002F]" />
            Formula Definitions
          </h3>
          <p className="text-xs text-zinc-500 mt-1">
            Define calculations that will be applied to COGS cards using this template
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowTemplates(!showTemplates)}
            className="text-xs"
          >
            <Blocks className="w-3 h-3 mr-1" /> Templates
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowPlayground(true)}
            disabled={formulas.length === 0}
            className="text-xs"
          >
            <Play className="w-3 h-3 mr-1" /> Playground
          </Button>
        </div>
      </div>

      {showTemplates && (
        <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-lg p-4 border border-zinc-200 dark:border-zinc-700">
          <h4 className="text-xs text-zinc-500 dark:text-zinc-400 mb-3">Quick Add Formula Templates:</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {FORMULA_TEMPLATES.map((template, idx) => (
              <button
                key={idx}
                onClick={() => applyTemplate(template)}
                className="text-left p-3 bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-700 hover:border-[#C3002F] transition-colors"
              >
                <p className="text-sm font-medium text-zinc-900 dark:text-white">{template.name}</p>
                <p className="text-xs text-zinc-500 mt-1">{template.description}</p>
                <code className="text-xs text-blue-500 dark:text-blue-400 mt-2 block">= {template.formula}</code>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {formulas.map((formula, index) => (
          <FormulaRow
            key={index}
            formula={formula}
            index={index}
            onUpdate={(updated) => updateFormula(index, updated)}
            onDelete={() => deleteFormula(index)}
            onMoveUp={() => moveFormula(index, -1)}
            onMoveDown={() => moveFormula(index, 1)}
            availableVariables={getAvailableVariables(index)}
            isFirst={index === 0}
            isLast={index === formulas.length - 1}
            onValidate={(f) => validateFormula(f, index)}
            validationResult={validationResults[index]}
          />
        ))}
      </div>

      <Button
        type="button"
        variant="outline"
        onClick={addFormula}
        className="w-full border-dashed border-zinc-300 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:border-zinc-400 dark:hover:border-zinc-500"
      >
        <Plus className="w-4 h-4 mr-2" /> Add Formula Step
      </Button>

      {showPlayground && (
        <FormulaPlayground
          formulas={formulas}
          pricingFields={pricingFields}
          onClose={() => setShowPlayground(false)}
        />
      )}
    </div>
  );
}
